import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject, linkedSignal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { createRxMutation, createRxResource, NotificationActions } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { ActionsSubject, Store } from '@ngrx/store';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, map, of, switchMap } from 'rxjs';

import { defaultBlightDayRange } from '../../blight-v2/shared/helpers/blight-helpers';
import { AppState } from '../../shared/models';
import { LoadStatus } from '../../shared/models/load-status';
import { SprayApplicationDialogService } from '../../shared/spray-applications/services/spray-application-dialog.service';
import { CustomerRightsService } from '../../shared/services/customer-rights.service';
import { DateService } from '../../shared/services/date.service';
import { CropYearService } from '../../shared/services/crop-year.service';
import { observeCustomerSwitches } from '../../shared/utils/customer-switch.utils';
import { withCustomerParams } from '../../shared/utils/customer-params.utils';
import { dispatchValidationErrorSnackbars } from '../../shared/utils/error-handling.utils';
import { cropYearChanged, openBlightIrrigation, plantDateUpdated } from '../../store/actions';
import { isCropYearReadOnly, selectCropYear } from '../../store/selectors';
import { BlightService } from '../../blight-v2/services/blight.service';
import { GrowthStageDialogComponent } from '../components/growth-stage-dialog/growth-stage-dialog.component';
import {
  CropFieldLatestGrowthStage,
  GrowthStageDialogData,
  GrowthStageRegistration,
  GrowthStageRegistrationRequest,
  GrowthStageRemovalRequest,
} from '../models/growth-stage.models';
import { mapDownyMildewCropFieldAdvice } from '../models/mappers';
import { DownyMildewCalculationStatus, DownyMildewData, DownyMildewDay } from '../models/models';
import {
  DownyMildewObservationDialogData,
  DownyMildewObservationRegistration,
  DownyMildewObservationRegistrationRequest,
  DownyMildewObservationRemovalRequest,
} from '../models/observation.models';
import { ObservationDialogComponent } from '../components/observation-dialog/observation-dialog.component';
import { DownyMildewNotificationService } from '../services/downy-mildew-notification.service';
import { DownyMildewService } from '../services/downy-mildew.service';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';
import { BaseCropFieldRegistration } from '../../shared/components/disease-overview-table.component/models/models';
import { SprayApplicationService } from '../../shared/spray-applications/services/spray-application.service';
import { SprayApplicationHistoryCropField } from '../../shared/spray-applications/models/spray-application.models';

function arraysEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

interface DownyMildewResourceParams {
  customerId: number;
  cropYear: number;
  fromDate: Date;
  toDate: Date;
  selectedCropFieldId?: number;
}

interface GrowthStageHistoryResourceParams {
  customerId: number;
  cropFieldIds: number[];
  cropYear: number;
}

interface ObservationHistoryResourceParams {
  customerId: number;
  cropFieldIds: number[];
  cropYear: number;
}

interface CropFieldDateUpdateRequest {
  cropFieldId: number;
  year: number;
  date: Date;
}

interface DownyMildewState {
  hasDownyMildewPremium: boolean;
  moduleActivationStatus: LoadStatus;
  fromDate?: Date;
  toDate?: Date;
  selectedCropFieldId?: number;
  growthStageCropFieldIds: number[];
  growthStageCropYear: number;
  observationCropFieldIds: number[];
  observationCropYear: number;
}

const initialState: DownyMildewState = {
  hasDownyMildewPremium: false,
  moduleActivationStatus: 'idle',
  fromDate: undefined,
  toDate: undefined,
  selectedCropFieldId: undefined,
  growthStageCropFieldIds: [],
  growthStageCropYear: new Date().getFullYear(),
  observationCropFieldIds: [],
  observationCropYear: new Date().getFullYear(),
};

export const DownyMildewSignalStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(() => ({
    cropYear: inject(Store<AppState>).selectSignal(selectCropYear),
  })),
  withResource(
    (store) => {
      const downyMildewService = inject(DownyMildewService);
      const sprayApplicationService = inject(SprayApplicationService);
      const appStore = inject(Store<AppState>);
      const customer = appStore.selectSignal(UserSelectors.selectCustomer);
      const downyMildewData = createRxResource<DownyMildewData[], DownyMildewResourceParams | undefined>({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => {
            if (!store.hasDownyMildewPremium() || !store.fromDate() || !store.toDate()) {
              return undefined;
            }

            // Extend date range by ±1 day for chart graph data continuity
            const fromDate = new Date(store.fromDate()!);
            const toDate = new Date(store.toDate()!);
            fromDate.setDate(fromDate.getDate() - 1);
            toDate.setDate(toDate.getDate() + 1);

            return {
              customerId,
              cropYear: store.cropYear(),
              fromDate,
              toDate,
            };
          }),
        stream: ({ params }) => {
          return downyMildewService
            .getAllDownyMildewCropFields(params.fromDate, params.toDate, params.cropYear, params.customerId)
            .pipe(
              switchMap((newData) => {
                const cropFieldIds = newData.map((cropField) => cropField.cropFieldId);
                if (!cropFieldIds.length) {
                  return of(newData);
                }

                return sprayApplicationService
                  .getSprayApplications(cropFieldIds, 'DownyMildew', params.customerId)
                  .pipe(
                    map((sprayApplications) => applySprayApplicationsToDownyMildewData(newData, sprayApplications)),
                    catchError(() => of(newData)),
                  );
              }),
              map((newData) => mergeDownyMildewData(downyMildewData.value(), newData)),
            );
        },
        defaultValue: [],
        errorMessage: 'errors.downyMildew.genericError',
      });
      const latestGrowthStages = createRxResource<
        CropFieldLatestGrowthStage[],
        { customerId: number; cropFieldIds: number[] } | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => ({
            customerId,
            cropFieldIds: store.growthStageCropFieldIds(),
          })),
        stream: ({ params }) =>
          params.cropFieldIds.length
            ? downyMildewService.getLatestGrowthStages(params.cropFieldIds, params.customerId)
            : of([]),
        defaultValue: [],
        errorMessage: 'errors.downyMildew.genericError',
      });
      const growthStageHistory = createRxResource<
        GrowthStageRegistration[],
        GrowthStageHistoryResourceParams | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => ({
            customerId,
            cropFieldIds: store.growthStageCropFieldIds(),
            cropYear: store.growthStageCropYear(),
          })),
        stream: ({ params }) =>
          params.cropFieldIds.length
            ? downyMildewService.getGrowthStageHistory(params.cropFieldIds, params.cropYear, params.customerId)
            : of([]),
        defaultValue: [],
        errorMessage: 'errors.downyMildew.genericError',
      });
      const observationHistory = createRxResource<
        DownyMildewObservationRegistration[],
        ObservationHistoryResourceParams | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => ({
            customerId,
            cropFieldIds: store.observationCropFieldIds(),
            cropYear: store.observationCropYear(),
          })),
        stream: ({ params }) =>
          params.cropFieldIds.length
            ? downyMildewService.getObservationHistory(params.cropFieldIds, params.cropYear, params.customerId)
            : of([]),
        defaultValue: [],
        errorMessage: 'errors.downyMildew.genericError',
      });

      return {
        downyMildewData,
        latestGrowthStages,
        growthStageHistory,
        observationHistory,
      };
    },
    {
      errorHandling: 'previous value',
    },
  ),
  withMutations((store) => {
    const downyMildewService = inject(DownyMildewService);
    const blightService = inject(BlightService);
    const localeService = inject(LocaleService);
    const appStore = inject(Store<AppState>);
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);

    const updateDownyMildewData = (cropFieldId: number, updater: (data: DownyMildewData) => DownyMildewData): void => {
      patchState(store, {
        downyMildewDataValue: untracked(() =>
          store.downyMildewDataValue().map((data) => (data.cropFieldId === cropFieldId ? updater(data) : data)),
        ),
      });
    };

    const applyCropFieldRegistrationUpdate = (registration: BaseCropFieldRegistration): void => {
      updateDownyMildewData(registration.cropFieldId, (data) => ({
        ...data,
        plantDate: registration.plantDate,
        emergenceDate: registration.emergenceDate,
        harvestDate: registration.harvestDate,
        isPlantDateConfirmed: registration.isPlantDateConfirmed,
      }));
    };

    const reemitSelectedDownyMildewData = (): void => {
      const selectedCropFieldId = untracked(() => store.selectedCropFieldId());
      if (!selectedCropFieldId) {
        return;
      }

      updateDownyMildewData(selectedCropFieldId, (data) => ({ ...data }));
    };

    return {
      recalculateDownyMildew: createRxMutation<number[], void>({
        operation: (cropFieldIds) =>
          cropFieldIds.length ? downyMildewService.recalculateDownyMildewAdvisories(cropFieldIds) : EMPTY,
        // No explicit refetch on success: recalculation updates are received via SignalR notifications.
        notification: { errorMessage: 'errors.downyMildew.genericError' },
      }),
      updatePlantDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updatePlantDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          reemitSelectedDownyMildewData();
        },
        notification: { errorMessage: 'errors.downyMildew.genericError' },
      }),
      updateEmergenceDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updateEmergenceDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          reemitSelectedDownyMildewData();
        },
        notification: { errorMessage: 'errors.downyMildew.genericError' },
      }),
      updateHarvestDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updateHarvestDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          reemitSelectedDownyMildewData();
        },
        notification: { errorMessage: 'errors.downyMildew.genericError' },
      }),
      growthStageRegistration: createRxMutation<GrowthStageRegistrationRequest, void>({
        operation: (request) => downyMildewService.registerGrowthStage(request),
        onSuccess: () => {
          store._latestGrowthStagesReload();
          store._growthStageHistoryReload();
        },
        notification: {
          successMessage: 'downyMildew.growthPhase.registrationSuccess',
          errorMessage: 'errors.downyMildew.growthStageError',
        },
      }),
      growthStageRemoval: createRxMutation<GrowthStageRemovalRequest, void>({
        operation: (request) => downyMildewService.deleteGrowthStage(request),
        onSuccess: () => {
          store._downyMildewDataReload();
          store._latestGrowthStagesReload();
          store._growthStageHistoryReload();
        },
        notification: {
          successMessage: 'downyMildew.growthPhase.removeSuccess',
          errorMessage: 'errors.downyMildew.growthStageDeleteError',
        },
      }),
      observationRegistration: createRxMutation<DownyMildewObservationRegistrationRequest, void>({
        operation: (request) => downyMildewService.registerObservation(request),
        onSuccess: () => {
          store._observationHistoryReload();
        },
        notification: {
          successMessage: 'downyMildew.observation.registrationSuccess',
          errorMessage: 'errors.downyMildew.observationError',
        },
      }),
      observationRemoval: createRxMutation<DownyMildewObservationRemovalRequest, void>({
        operation: (request) => downyMildewService.deleteObservation(request),
        onSuccess: () => {
          store._downyMildewDataReload();
          store._observationHistoryReload();
        },
        notification: {
          successMessage: 'downyMildew.observation.removeSuccess',
          errorMessage: 'errors.downyMildew.observationDeleteError',
        },
      }),
    };
  }),
  withComputed(
    (
      store,
      dateService = inject(DateService),
      localeService = inject(LocaleService),
      appStore = inject(Store<AppState>),
    ) => {
      const customer = appStore.selectSignal(UserSelectors.selectCustomer);
      const sortedDownyMildewAdvices = computed((): CropFieldAdvice[] =>
        store
          .downyMildewDataValue()
          .map(mapDownyMildewCropFieldAdvice)
          .sort((a, b) => a.cropFieldName.localeCompare(b.cropFieldName)),
      );
      const eligibleDownyMildewCropFields = computed((): CropFieldAdvice[] =>
        sortedDownyMildewAdvices().filter(
          (cropField) => cropField.isCropFieldVisible && isCropFieldEligibleForRecalculation(cropField, dateService),
        ),
      );

      return {
        downyMildewCalculationDate: linkedSignal<DownyMildewData[], Date | null>({
          source: () => store.downyMildewDataValue(),
          computation: (data, previous): Date | null => {
            const newValue = data.reduce(
              (minDate, item) => {
                const calculatedOn = item.downyMildewResult?.calculatedOn ?? null;
                return calculatedOn && (!minDate || calculatedOn < minDate) ? calculatedOn : minDate;
              },
              null as Date | null,
            );
            return newValue ?? previous?.value ?? null;
          },
        }),
        downyMildewAdvices: computed((): CropFieldAdvice[] => sortedDownyMildewAdvices()),
        customer,
        pageTitle: computed(() => localeService.translate('downyMildew.advice.pageTitle', { name: customer()?.name })),
        downyMildewMinAdviceDate: linkedSignal<CropFieldAdvice[], Date | undefined>({
          source: () => sortedDownyMildewAdvices(),
          computation: (cropFields, previous): Date | undefined => {
            let minDate: Date | undefined = undefined;

            cropFields.forEach((cropField) => {
              const earliestAdviceDate = getEarliestAdviceDate(cropField);
              const candidateDates = [cropField.plantDate, earliestAdviceDate].filter((date): date is Date => !!date);

              candidateDates.forEach((date) => {
                if (!minDate || date < minDate) {
                  minDate = date;
                }
              });
            });

            return minDate ?? previous?.value;
          },
        }),
        eligibleDownyMildewCropFieldIds: computed((): number[] =>
          eligibleDownyMildewCropFields().map((cropField) => cropField.cropFieldId),
        ),
        hasEligibleDownyMildewCropFields: computed((): boolean => eligibleDownyMildewCropFields().length > 0),
        isDownyMildewCropFieldsSyncing: computed((): boolean =>
          store.downyMildewDataValue().some((data) => data.downyMildewResult?.calculationStatus === 'initiated'),
        ),
        selectedDownyMildewData: computed((): DownyMildewData | undefined =>
          store.downyMildewDataValue().find((d) => d.cropFieldId === store.selectedCropFieldId()),
        ),
        selectedCropFieldAdvice: computed((): CropFieldAdvice | undefined =>
          sortedDownyMildewAdvices().find((a) => a.cropFieldId === store.selectedCropFieldId()),
        ),
        hasSelectedCropFieldAdvices: computed((): boolean => {
          const advice = sortedDownyMildewAdvices().find((a) => a.cropFieldId === store.selectedCropFieldId());
          return (advice?.advices?.length ?? 0) > 0;
        }),
        isPlantDateConfirmed: computed((): boolean => {
          const data = store.downyMildewDataValue().find((d) => d.cropFieldId === store.selectedCropFieldId());
          return data?.isPlantDateConfirmed ?? false;
        }),
        isSelectedCropFieldIneligibleForRecalculation: computed((): boolean => {
          const data = store.downyMildewDataValue().find((d) => d.cropFieldId === store.selectedCropFieldId());
          if (!data) return false;
          const plantDateInFuture = data.plantDate ? dateService.isInTheFuture(new Date(data.plantDate)) : false;
          const harvestDateInPast = data.harvestDate ? dateService.isInThePast(new Date(data.harvestDate)) : false;
          return plantDateInFuture || harvestDateInPast;
        }),
      };
    },
  ),
  withMethods(
    (
      store,
      appStore = inject(Store<AppState>),
      dateService = inject(DateService),
      dialogService = inject(DialogService),
      localeService = inject(LocaleService),
      sprayApplicationDialogService = inject(SprayApplicationDialogService),
    ) => {
      const setCalculatingStatus = (cropFieldId: number, status: DownyMildewCalculationStatus): void => {
        const currentData = untracked(() => store.downyMildewDataValue());
        const updatedData = currentData.map((data) =>
          data.cropFieldId === cropFieldId ? setCropFieldStatus(data, status) : data,
        );
        patchState(store, { downyMildewDataValue: updatedData });
      };

      return {
        patchSelectedDates(dates: Date[]): void {
          const fromDate = dates[0];
          const toDate = dates.at(-1);

          if (!fromDate || !toDate) {
            return;
          }

          patchState(store, { fromDate, toDate });
        },
        resetDownyMildewCropFieldDefaultRange(cropFieldId: number): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            store.cropYear(),
          );

          const from = dates[0];
          const to = dates.at(-1)!;

          patchState(store, { selectedCropFieldId: cropFieldId, fromDate: from, toDate: to });
        },
        resetForCropYear(cropYear: number): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            cropYear,
          );

          patchState(store, {
            selectedCropFieldId: undefined,
            fromDate: dates[0],
            toDate: dates.at(-1),
            growthStageCropFieldIds: [],
            growthStageCropYear: cropYear,
            observationCropFieldIds: [],
            observationCropYear: cropYear,
            downyMildewDataValue: [],
            latestGrowthStagesValue: [],
            growthStageHistoryValue: [],
            observationHistoryValue: [],
          });
        },
        openSpraysDialog(
          sprayType: number,
          advices: CropFieldAdvice[],
          actionDate: Date | undefined,
        ): void {
          sprayApplicationDialogService.openFromCropFields('downy-mildew', advices, sprayType, actionDate);
        },
        openBlightIrrigation(advices: CropFieldAdvice[], actionDate: Date | undefined): void {
          appStore.dispatch(openBlightIrrigation({ advices, actionDate }));
        },
        openGrowthStageDialog(
          advices: CropFieldAdvice[],
          actionDate: Date | undefined,
          preferredBbchCode?: string,
        ): void {
          const cropYear = advices[0]?.cropYear;
          if (cropYear == null) {
            return;
          }

          const dialogData: GrowthStageDialogData = {
            title: 'downyMildew.growthPhase.dialogTitle',
            advices,
            actionDate: actionDate ?? new Date(),
            cropYear,
            preferredBbchCode,
            readOnly: isCropYearReadOnly(cropYear, dateService.currentYear()),
          };

          dialogService.fullscreenDialog(GrowthStageDialogComponent, dialogData, {
            width: '800px',
          });
        },
        openObservationDialog(advices: CropFieldAdvice[], actionDate: Date | undefined): void {
          const cropYear = advices[0]?.cropYear;
          if (cropYear == null) {
            return;
          }

          const dialogData: DownyMildewObservationDialogData = {
            title: 'downyMildew.observation.dialogTitle',
            cropFields: advices.map((advice) => ({
              cropFieldId: advice.cropFieldId,
              cropFieldName: advice.cropFieldName,
              areaInHectares: advice.amountOfHectares,
            })),
            actionDate: actionDate ?? new Date(),
            cropYear,
            readOnly: isCropYearReadOnly(cropYear, dateService.currentYear()),
          };

          dialogService.fullscreenDialog(ObservationDialogComponent, dialogData, {
            width: '800px',
          });
        },
        registerGrowthStage(request: GrowthStageRegistrationRequest): void {
          store.growthStageRegistration(request);
        },
        registerObservation(request: DownyMildewObservationRegistrationRequest): void {
          store.observationRegistration(request);
        },
        setGrowthStageSelection(cropFieldIds: number[], cropYear: number): void {
          const currentIds = untracked(() => store.growthStageCropFieldIds());
          const currentYear = untracked(() => store.growthStageCropYear());
          const idsChanged = !arraysEqual(currentIds, cropFieldIds);
          const yearChanged = currentYear !== cropYear;

          if (idsChanged || yearChanged) {
            patchState(store, {
              ...(idsChanged && { growthStageCropFieldIds: [...cropFieldIds] }),
              ...(yearChanged && { growthStageCropYear: cropYear }),
            });
          }
        },
        setObservationSelection(cropFieldIds: number[], cropYear: number): void {
          const currentIds = untracked(() => store.observationCropFieldIds());
          const currentYear = untracked(() => store.observationCropYear());
          const idsChanged = !arraysEqual(currentIds, cropFieldIds);
          const yearChanged = currentYear !== cropYear;

          if (idsChanged || yearChanged) {
            patchState(store, {
              ...(idsChanged && { observationCropFieldIds: [...cropFieldIds] }),
              ...(yearChanged && { observationCropYear: cropYear }),
            });
          }
        },
        removeGrowthStage(request: GrowthStageRemovalRequest): void {
          store.growthStageRemoval(request);
        },
        removeObservation(request: DownyMildewObservationRemovalRequest): void {
          store.observationRemoval(request);
        },
        setCalculatingInitiated(cropFieldId: number): void {
          setCalculatingStatus(cropFieldId, 'initiated');
        },
        notifyCalculationFailed(cropFieldId: number): void {
          setCalculatingStatus(cropFieldId, 'failed');

          appStore.dispatch(
            NotificationActions.errorSnackbar({
              message: localeService.translate('errors.downyMildew.genericCalculationError'),
            }),
          );
        },
        selectCropField(cropFieldId: number): void {
          patchState(store, { selectedCropFieldId: cropFieldId });
        },
      };
    },
  ),
  withHooks({
    onInit(store): void {
      const appStore = inject(Store<AppState>);
      const actions = inject(ActionsSubject);
      const customerRightsService = inject(CustomerRightsService);
      const dateService = inject(DateService);
      const cropYearService = inject(CropYearService);
      const notificationService = inject(DownyMildewNotificationService);

      combineLatest([
        appStore.select(UserSelectors.selectCustomer).pipe(map((customer) => customer?.id), distinctUntilChanged()),
        cropYearService.cropYear$,
      ])
        .pipe(
          distinctUntilChanged(([previousCustomerId, previousCropYear], [customerId, cropYear]) =>
            previousCustomerId === customerId && previousCropYear === cropYear),
          switchMap(([customerId, cropYear]) => {
            patchState(store, {
              hasDownyMildewPremium: false,
              moduleActivationStatus: customerId ? 'loading' : 'idle',
            });
            return customerId
              ? customerRightsService.getModuleActivation('downy-mildew', cropYear).pipe(
                catchError(() => {
                  patchState(store, { moduleActivationStatus: 'error' });
                  return EMPTY;
                }),
              )
              : EMPTY;
          }),
          takeUntilDestroyed(),
        )
        .subscribe((hasDownyMildewPremium) => {
          patchState(store, { hasDownyMildewPremium, moduleActivationStatus: 'loaded' });
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            store.cropYear(),
          );
          store.patchSelectedDates(dates);
        });

      observeCustomerSwitches(appStore)
        .pipe(
          filter(({ previousCustomerId }) => previousCustomerId !== null),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          patchState(store, {
            selectedCropFieldId: undefined,
            downyMildewDataValue: [],
            latestGrowthStagesValue: [],
            growthStageHistoryValue: [],
            observationHistoryValue: [],
            growthStageCropFieldIds: [],
            observationCropFieldIds: [],
          });
          store._downyMildewDataReload();
          store._latestGrowthStagesReload();
          store._growthStageHistoryReload();
          store._observationHistoryReload();
        });

      notificationService.onInitiated
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.setCalculatingInitiated(event.CropFieldId));

      notificationService.onCompleted
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.resetDownyMildewCropFieldDefaultRange(event.CropFieldId));

      notificationService.onFailed
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.notifyCalculationFailed(event.CropFieldId));

      actions
        .pipe(
          filter((action) => action.type === plantDateUpdated.type),
          takeUntilDestroyed(),
        )
        .subscribe(() => store._downyMildewDataReload());

      actions
        .pipe(
          filter((action) => action.type === cropYearChanged.type),
          map((action) => action as ReturnType<typeof cropYearChanged>),
          takeUntilDestroyed(),
        )
        .subscribe(({ cropYear }) => store.resetForCropYear(cropYear));
    },
  }),
);

const getEarliestAdviceDate = (cropField: CropFieldAdvice): Date | undefined => {
  let minAdviceDate: Date | undefined = undefined;

  for (const advice of cropField.advices) {
    if (!minAdviceDate || advice.date < minAdviceDate) {
      minAdviceDate = advice.date;
    }
  }

  const todaysAdviceDate = cropField.todaysAdvice?.date;
  if (todaysAdviceDate && (!minAdviceDate || todaysAdviceDate < minAdviceDate)) {
    minAdviceDate = todaysAdviceDate;
  }

  return minAdviceDate;
};

const mergeDownyMildewData = (oldData: DownyMildewData[], newData: DownyMildewData[]): DownyMildewData[] => {
  const mergedData = new Map<number, DownyMildewData>();

  oldData.forEach((oldDownyMildewData) => {
    mergedData.set(oldDownyMildewData.cropFieldId, oldDownyMildewData);
  });

  newData.forEach((newDownyMildewData) => {
    const oldDownyMildewData = mergedData.get(newDownyMildewData.cropFieldId);

    if (oldDownyMildewData) {
      mergedData.set(newDownyMildewData.cropFieldId, {
        ...newDownyMildewData,
        downyMildewResult: {
          ...newDownyMildewData.downyMildewResult,
          days: oldDownyMildewData.downyMildewResult
            ? mergeDownyMildewDays(
              oldDownyMildewData.downyMildewResult.days ?? [],
              newDownyMildewData.downyMildewResult?.days ?? [],
            )
            : [],
        },
      });
    } else {
      mergedData.set(newDownyMildewData.cropFieldId, newDownyMildewData);
    }
  });

  return Array.from(mergedData.values());
};

const applySprayApplicationsToDownyMildewData = (
  downyMildewData: DownyMildewData[],
  sprayApplicationsByCropField: SprayApplicationHistoryCropField[],
): DownyMildewData[] => {
  const sprayApplicationsByDateAndCropField = new Map<number, Map<number, Date>>();

  sprayApplicationsByCropField.forEach((cropField) => {
    sprayApplicationsByDateAndCropField.set(cropField.id, buildLatestSprayApplicationByDay(cropField));
  });

  return downyMildewData.map((cropField) => {
    const sprayApplicationsByDay = sprayApplicationsByDateAndCropField.get(cropField.cropFieldId);
    if (!sprayApplicationsByDay) {
      return cropField;
    }

    return {
      ...cropField,
      downyMildewResult: {
        ...cropField.downyMildewResult,
        days: cropField.downyMildewResult.days.map((day) => ({
          ...day,
          sprayTime: getMostRecentDate(sprayApplicationsByDay.get(getUtcDateKey(day.date)), day.sprayTime),
        })),
        today: cropField.downyMildewResult.today
          ? {
            ...cropField.downyMildewResult.today,
            sprayTime: getMostRecentDate(
              sprayApplicationsByDay.get(getUtcDateKey(cropField.downyMildewResult.today.date)),
              cropField.downyMildewResult.today.sprayTime,
            ),
          }
          : undefined,
      },
    };
  });
};

const buildLatestSprayApplicationByDay = (cropField: SprayApplicationHistoryCropField): Map<number, Date> => {
  const latestSprayByDay = new Map<number, Date>();

  cropField.sprayApplications.forEach((sprayApplication) => {
    const dayKey = getUtcDateKey(sprayApplication.applicationDate);
    const currentLatest = latestSprayByDay.get(dayKey);
    if (!currentLatest || sprayApplication.applicationDate > currentLatest) {
      latestSprayByDay.set(dayKey, sprayApplication.applicationDate);
    }
  });

  return latestSprayByDay;
};

const getMostRecentDate = (firstDate?: Date, secondDate?: Date): Date | undefined => {
  if (!firstDate) {
    return secondDate;
  }

  if (!secondDate) {
    return firstDate;
  }

  return firstDate > secondDate ? firstDate : secondDate;
};

const setCropFieldStatus = (cropField: DownyMildewData, status: DownyMildewCalculationStatus): DownyMildewData => {
  return {
    ...cropField,
    downyMildewResult: {
      ...cropField.downyMildewResult,
      calculationStatus: status,
    },
  };
};

const mergeDownyMildewDays = (oldDays: DownyMildewDay[], newDays: DownyMildewDay[]): DownyMildewDay[] => {
  const mergedDays = new Map<number, DownyMildewDay>();

  oldDays.forEach((day) => {
    mergedDays.set(getDayKey(day.date), day);
  });

  newDays.forEach((day) => {
    mergedDays.set(getDayKey(day.date), day);
  });

  return Array.from(mergedDays.values());
};

const getDayKey = (date: Date): number => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.getTime();
};

const getUtcDateKey = (date: Date): number => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const isCropFieldEligibleForRecalculation = (cropField: CropFieldAdvice, dateService: DateService): boolean =>
  (!cropField.plantDate || !dateService.isInTheFuture(cropField.plantDate)) &&
  (!cropField.harvestDate || !dateService.isInThePast(cropField.harvestDate));
